import { getConfig } from '../config.js';
import { getClientSlug } from '../helpers/clientSlug';

const GUEST_STORAGE_KEY = 'sd_guest_checkout';
const GUEST_SESSION_MINUTES = 16;

const parseGuestData = () => {
	try {
		const raw = localStorage.getItem(GUEST_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (_) {
		localStorage.removeItem(GUEST_STORAGE_KEY);
		return null;
	}
};

const isValidStoredGuest = (data) => {
	if (!data?.guest_token || !data?.expires_at) return false;
	return new Date(data.expires_at.replace(' ', 'T')).getTime() > Date.now();
};

export const getStoredGuestToken = () => {
	const data = parseGuestData();
	return isValidStoredGuest(data) ? data.guest_token : '';
};

export const hasStoredGuestToken = () => Boolean(getStoredGuestToken());

export const clearGuestToken = () => {
	localStorage.removeItem(GUEST_STORAGE_KEY);
};

const getRollingExpiresAt = () => {
	return new Date(Date.now() + GUEST_SESSION_MINUTES * 60 * 1000).toISOString();
};

const saveGuestTokenData = (guestToken, expiresAt) => {
	if (guestToken && expiresAt) {
		localStorage.setItem(
			GUEST_STORAGE_KEY,
			JSON.stringify({
				guest_token: guestToken,
				expires_at: expiresAt,
			}),
		);
	}

	return guestToken;
};

const saveGuestToken = (response) => {
	const guestToken = response?.guest_token || response?.data?.guest_token;
	const expiresAt = response?.data?.expires_at || getRollingExpiresAt();

	return saveGuestTokenData(guestToken, expiresAt);
};

export const ensureGuestToken = async (options = {}) => {
	const storedToken = getStoredGuestToken();
	if (storedToken) return storedToken;

	const webLink = getConfig().REACT_APP_WEB_LINK;
	const slug = getClientSlug();
	const payload = slug
		? { action: 'generate_guest_token', slug }
		: { action: 'generate_guest_token' };

	const result = await fetch(webLink, {
		method: 'post',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(payload),
		signal: options.signal,
	});

	const response = await result.json();
	if (response.type?.toLowerCase() !== 'success') {
		throw new Error(response.message || 'Unable to start guest session');
	}

	return saveGuestToken(response);
};

export default async function guestApiService(query, options = {}) {
	const webLink = getConfig().REACT_APP_WEB_LINK;
	const guestToken = await ensureGuestToken(options);
	const slug = getClientSlug();
	const payload = slug ? { ...query, slug } : query;

	const result = await fetch(webLink, {
		method: 'post',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			'guest-token': guestToken,
		},
		body: JSON.stringify(payload),
		signal: options.signal,
	});

	const response = await result.json();
	if (
		response.type?.toLowerCase() === 'error' &&
		response.error_code === 'guest_session_expired'
	) {
		clearGuestToken();
	} else if (response.type?.toLowerCase() === 'success') {
		saveGuestTokenData(guestToken, response?.data?.expires_at || getRollingExpiresAt());
	}

	return response;
}
